import { Download, Eye } from "lucide-react";

import { Badge } from "@krea/shared/design-system/Badge";
import FileSection from "@krea/shared/design-system/file-section";
import FileSectionAction from "@krea/shared/design-system/file-section/FileSectionAction";
import { toast } from "@krea/shared/design-system/utils/toast";
import { downloadFile } from "@krea/shared/utils/downloadFile";

import type { FileAttachment } from "./types";

type CategoryConfig = {
  label: string;
  badgeVariant: "warning" | "success" | "secondary";
};

type FilesSectionProps = {
  accountingDataFiles: FileAttachment[] | null;
  annualReportFiles: FileAttachment[] | null;
  bankingDataFiles: FileAttachment[] | null;
};

export const FilesSection = ({
  accountingDataFiles,
  annualReportFiles,
  bankingDataFiles,
}: FilesSectionProps) => {
  const categories: Array<{ config: CategoryConfig; files: FileAttachment[] }> =
    [
      {
        config: { label: "Accounting data", badgeVariant: "warning" },
        files: accountingDataFiles ?? [],
      },
      {
        config: { label: "Annual report", badgeVariant: "success" },
        files: annualReportFiles ?? [],
      },
      {
        config: { label: "Banking data", badgeVariant: "secondary" },
        files: bankingDataFiles ?? [],
      },
    ];

  const hasAnyFiles = categories.some((c) => c.files.length > 0);

  if (!hasAnyFiles) {
    return (
      <div className="py-4 text-center text-helper">No files uploaded</div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {categories.map(
        ({ config, files }) =>
          files.length > 0 && (
            <div key={config.label} className="flex flex-col gap-2">
              <Badge variant={config.badgeVariant}>{config.label}</Badge>
              {files.map((file) => (
                <FileSection
                  key={file.id}
                  title={file.name}
                  actions={
                    <div className="flex items-center">
                      <FileSectionAction
                        title="View file"
                        Icon={Eye}
                        onClick={() => {
                          const url = new URL(file.url);
                          url.searchParams.set("download", "false");
                          window.open(
                            url.toString(),
                            "_blank",
                            "noopener,noreferrer",
                          );
                        }}
                      />
                      <FileSectionAction
                        title="Download file"
                        Icon={Download}
                        onClick={() =>
                          downloadFile(file.url, file.name).catch(() => {
                            toast.error("Failed to download file.");
                          })
                        }
                      />
                    </div>
                  }
                />
              ))}
            </div>
          ),
      )}
    </div>
  );
};
